import type { AccountView, HistoryPoint, HistoryRange, HistoryRead, LedgerEntry, MandateView, OrderView, PendingView, SecuritiesOrderStep, Valuation, ValuationHolding } from "../api/types.js";
import { bankPercent, calendarDate, formatMoney, isMoney, signedAmount, unitsText, whenAhead, whenPast } from "../lib/format.js";
import type { IconId } from "../lib/icon.js";
import { asOfWords, formatRate, pricesNote } from "./account-model.js";
import { accountLabel } from "./edit-model.js";
import { activeSoonest } from "./mandates-model.js";
import type { BundleRead } from "./receipt-model.js";
import { dateWords, dayMonth, fullDate, mandateAmount, ruleIcon, rowRule, shortDate } from "./schedule-words.js";

/**
 * The Invest tab's model (DCTA-229; docs/design/v3/ux-specs/10-invest.md §3–§5, §11): what the customer's shares are worth
 * and how that moved, what they hold, what they bought, what is about to happen.
 *
 * It composes the bank's reads and FORMATS them. It never adds, subtracts or derives a figure: the total is the valuation's,
 * the change over a range is the history read's own, the cash to invest is the trading account's available balance, a
 * held buy's amount is the pending line's (§11 acceptance 4 — the test proves every figure is a bank string). The only
 * numbers turned into numbers are the chart's points, and only by the view, for geometry.
 *
 * Each optional read has its fallback (§4.3): no history read → no chart and no range control, the hero shows the
 * valuation's day change; no orders read → the trading account's own ledger lines, "Filled". A read that FAILED is never
 * shown as empty: Orders and Coming up say "none" only when the reads behind them answered.
 */

export const RANGES: readonly HistoryRange[] = ["1w", "1m", "6m", "1y"];

/** The range control's words (§8): the visible label, what a screen reader hears after it, and the change line's suffix. */
export const RANGE_WORDS: Readonly<Record<HistoryRange, { readonly label: string; readonly name: string; readonly past: string }>> = {
  "1w": { label: "1W", name: "1 week", past: "past week" },
  "1m": { label: "1M", name: "1 month", past: "past month" },
  "6m": { label: "6M", name: "6 months", past: "past 6 months" },
  "1y": { label: "1Y", name: "1 year", past: "past year" },
};

/** A history read as the controller saw it: the bank has none (404), it failed or broke the contract, or it answered. */
export type HistoryState = { readonly kind: "absent" } | { readonly kind: "failed" } | { readonly kind: "ok"; readonly read: HistoryRead };

/** Read C, or its fallback: the trading account's securities lines with (when they could be read) their bundle and receipt. */
export type OrdersRead = { readonly kind: "route"; readonly orders: readonly OrderView[] } | { readonly kind: "ledger"; readonly lines: readonly FilledLine[] };
export interface FilledLine {
  readonly entry: LedgerEntry;
  readonly bundle?: BundleRead;
}

/** §4.3 rung 3: the trading account's executed buys — its securities debits (a hold is an internal line), the newest three. */
export function filledLines(entries: readonly LedgerEntry[]): LedgerEntry[] {
  return entries.filter((e) => e.channel === "securities" && e.direction === "debit").slice(0, 3);
}

export interface InvestReads {
  readonly valuation: Valuation;
  /** `GET /v1/portfolio`'s trading account (absent: the customer has none). */
  readonly settlement: AccountView | undefined;
  /** `GET /v1/gateway/pending` — undefined when it could not be read. */
  readonly pending: readonly PendingView[] | undefined;
  /** `GET /v1/mandates?destination_id={trading account}&status=active` — undefined when it could not be read. */
  readonly mandates: readonly MandateView[] | undefined;
  /** undefined when neither read C nor its ledger fallback could be read. */
  readonly orders: OrdersRead | undefined;
  readonly history: HistoryState;
  readonly range: HistoryRange;
  /** The customer locked their money (any account the bank says is locked). */
  readonly locked: boolean;
  /** The identity check has lapsed, where its refresh exists (Home's rule). */
  readonly lapsed: boolean;
  /** The bank's now: "today" and "tomorrow" are the bank's days. */
  readonly nowMs: number;
}

export type Tone = "up" | "down" | "flat";
export interface Change {
  readonly pct: string;
  readonly tone: Tone;
  readonly amount: string;
  readonly words: string;
}
export interface HeroModel {
  readonly total: string;
  readonly demo: boolean;
  readonly change?: Change;
}

export interface ChartPoint {
  /** The bank's calendar day. */
  readonly on: string;
  /** The bank's decimal string — the view turns it into a height, nothing else does anything with it. */
  readonly figure: string;
  readonly money: string;
  /** The reading's label: "Wed 2 Sep" (1Y: "Wed 2 Sep 2025"). */
  readonly day: string;
  /** The axis's label: "2 Sep" (1Y: "2 Sep 2025"). */
  readonly axis: string;
  /** The slider's value text: "Wed 2 Sep, S$2,296.46". */
  readonly text: string;
}
export interface ChartModel {
  readonly points: readonly ChartPoint[];
  /** The plot's accessible name: "What your shares are worth, past month". */
  readonly label: string;
  /** The figcaption: from, to, the high and the low, demo prices. */
  readonly summary: string;
  /** The hidden table's second column: "Value" or "Price". */
  readonly column: string;
  readonly high?: { readonly index: number; readonly text: string };
  readonly low?: { readonly index: number; readonly text: string };
}
/** none: the bank has no history read (no chart, no range control); few: under 4 points (the hero alone); failed: Retry. */
export type ChartSlot = { readonly kind: "none" } | { readonly kind: "few" } | { readonly kind: "failed" } | { readonly kind: "chart"; readonly chart: ChartModel };

export interface HoldingRow {
  readonly id: string;
  readonly ticker: string;
  readonly name: string;
  readonly units: string;
  readonly price: string;
  readonly value: string;
  readonly since?: { readonly text: string; readonly loss: boolean };
}
export interface CashRow {
  readonly name: string;
  readonly amount: string;
  /** One line per buy waiting for its market: "S$500.00 held for an order" — the bank's amount, never a difference. */
  readonly held: readonly string[];
}
export interface OrderRow {
  /** The receipt's key: "bundle:step" (an operation), or a ledger ref when the bundle could not be read. */
  readonly key: string;
  readonly ticker?: string;
  readonly name: string;
  readonly units?: string;
  readonly price?: string;
  readonly when: string;
  readonly amount: string;
  readonly state: "Settling" | "Settled" | "Filled";
}
export interface OrdersModel {
  readonly rows: readonly OrderRow[];
}
export type ComingRow =
  | { readonly kind: "buy"; readonly ic: IconId; readonly key: string; readonly title: string; readonly when: string; readonly amount: string; readonly upTo: boolean }
  | { readonly kind: "topup"; readonly ic: IconId; readonly mandateId: string; readonly title: string; readonly rule: string; readonly when: string; readonly amount: string };
export interface ComingModel {
  readonly rows: readonly ComingRow[];
}
export interface Banners {
  readonly locked: boolean;
  readonly lapsed: boolean;
}

export type InvestModel =
  | { readonly kind: "no-account" }
  | { readonly kind: "empty"; readonly banners: Banners; readonly cash?: CashRow; readonly orders?: OrdersModel; readonly coming?: ComingModel }
  | {
      readonly kind: "holdings";
      readonly banners: Banners;
      readonly hero: HeroModel;
      readonly chart: ChartSlot;
      readonly range: HistoryRange;
      readonly note?: string;
      readonly holdings: readonly HoldingRow[];
      readonly cash?: CashRow;
      readonly orders?: OrdersModel;
      readonly coming?: ComingModel;
    };

/**
 * Still waiting: the bank's `waiting`, else (an older bank) not expired — the chat's own rule for "What payments are
 * waiting?" (`pendingAnswer` in reads.ts, the assistant's code, left untouched), so the two never disagree.
 */
const stillWaiting = (p: PendingView): boolean => p.waiting ?? !p.expired;

/** A gain or a loss the bank states ("-19.80"): the contract's money string, a minus allowed. */
const isSignedMoney = (v: string): boolean => isMoney(v.replace(/^-/, ""));

const toneOf = (pct: string): Tone => (!/[1-9]/.test(pct) ? "flat" : pct.startsWith("-") ? "down" : "up");

/** A change the bank stated (a value and its percentage), or none when it did not state one in the contract's shape. */
function changeOf(value: string | undefined, pct: string | undefined, currency: string, words: string): Change | undefined {
  if (value === undefined || pct === undefined || !isSignedMoney(value)) return undefined;
  const shown = bankPercent(pct);
  return shown === undefined ? undefined : { pct: shown, tone: toneOf(pct), amount: signedAmount(value, currency), words };
}

const figureOf = (p: HistoryPoint | undefined): string | undefined => p?.value ?? p?.price;

/**
 * A history read (A or B) as the chart draws it. Every point must be a bank calendar day and a canonical money string —
 * a read that is not the contract's is a failed read (the plot says so and offers Retry), never a line guessed from it.
 * The high and the low are the bank's, each labelled at its own day; under 4 points there is no chart (the hero alone).
 */
export function chartOf(
  read: HistoryRead,
  o: { readonly range: HistoryRange; readonly currency: string; readonly label: string; readonly column: string; readonly demo: boolean },
): Exclude<ChartSlot, { readonly kind: "none" }> {
  if (!Array.isArray(read.points)) return { kind: "failed" };
  const year = o.range === "1y";
  const points: ChartPoint[] = [];
  for (const p of read.points) {
    const figure = figureOf(p);
    if (typeof p?.on !== "string" || dateWords(p.on) === undefined || figure === undefined || !isMoney(figure)) return { kind: "failed" };
    const money = formatMoney(figure, o.currency);
    const day = year ? fullDate(p.on) : shortDate(p.on);
    points.push({ on: p.on, figure, money, day, axis: year ? calendarDate(p.on) : dayMonth(p.on), text: `${day}, ${money}` });
  }
  const first = points[0];
  const last = points[points.length - 1];
  if (points.length < 4 || first === undefined || last === undefined) return { kind: "few" };
  const extreme = (x: HistoryPoint | undefined): { readonly index: number; readonly money: string } | undefined => {
    const f = figureOf(x);
    const index = x === undefined ? -1 : points.findIndex((p) => p.on === x.on);
    return f === undefined || !isMoney(f) || index < 0 ? undefined : { index, money: formatMoney(f, o.currency) };
  };
  const hi = extreme(read.high);
  const lo = extreme(read.low);
  const words = RANGE_WORDS[o.range];
  const extremes = [hi === undefined ? undefined : `high ${hi.money}`, lo === undefined ? undefined : `low ${lo.money}`].filter((x) => x !== undefined).join(", ");
  const said = extremes === "" ? "" : ` ${extremes.charAt(0).toUpperCase()}${extremes.slice(1)}.`;
  return {
    kind: "chart",
    chart: {
      points,
      label: `${o.label}, ${words.past}`,
      summary: `From ${first.money} to ${last.money} over the ${words.past.replace("past ", "last ")}.${said}${o.demo ? " Demo prices." : ""}`,
      column: o.column,
      ...(hi === undefined ? {} : { high: { index: hi.index, text: `High ${hi.money}` } }),
      ...(lo === undefined ? {} : { low: { index: lo.index, text: `Low ${lo.money}` } }),
    },
  };
}

/** The prices are demo prices: the valuation's own source says mock, or the history read says demo. */
function demoOf(v: Valuation, h: HistoryState): boolean {
  return v.source === "mock" || (h.kind === "ok" && h.read.price_kind === "demo");
}

function holdingRow(h: ValuationHolding, base: string): HoldingRow {
  const pct = h.unrealised_pl_pct === undefined ? undefined : bankPercent(h.unrealised_pl_pct);
  return {
    id: h.instrument_id,
    ticker: h.ticker,
    name: h.name,
    units: unitsText(h.units),
    price: formatMoney(h.price, h.currency),
    value: formatMoney(h.market_value_sgd, base),
    ...(pct === undefined || h.unrealised_pl_pct === undefined ? {} : { since: { text: pct, loss: toneOf(h.unrealised_pl_pct) === "down" } }),
  };
}

/** A buy the bank holds for its market, as the tab says it: its receipt's key, what it buys, when it goes, the bank's amount. */
interface HeldBuy {
  readonly key: string;
  readonly name: string;
  readonly until: string;
  readonly amount: string;
  readonly upTo: boolean;
}

/** The buys the bank holds for their market: a securities line, a buy, still waiting (the chat's rule), with a time and an amount. */
function heldBuys(pending: readonly PendingView[]): HeldBuy[] {
  return pending.filter(stillWaiting).flatMap((p) =>
    (p.lines ?? []).flatMap((l) => {
      const until = l.hold_until ?? p.hold_until;
      const name = l.to?.name;
      if (l.type !== "securities_order" || (l.side ?? "buy") !== "buy" || until === undefined || l.amount === undefined || name === undefined) return [];
      return [{ key: `${p.bundle_id}:${l.step_id}`, name, until, amount: formatMoney(l.amount.value, l.amount.currency), upTo: l.amount_is_max === true }];
    }),
  );
}

function cashRow(a: AccountView, pending: readonly PendingView[]): CashRow {
  return {
    name: accountLabel(a),
    amount: formatMoney(a.available_balance, a.currency),
    held: heldBuys(pending).map((b) => `${b.amount} held for an order`),
  };
}

function comingOf(pending: readonly PendingView[] | undefined, mandates: readonly MandateView[] | undefined, nowMs: number): ComingModel | undefined {
  const buys: ComingRow[] = heldBuys(pending ?? []).map((b) => ({ kind: "buy", ic: "i-clock", key: b.key, title: `Buy ${b.name}`, when: whenAhead(b.until, nowMs), amount: b.amount, upTo: b.upTo }));
  const topups: ComingRow[] = activeSoonest(mandates ?? []).map((m) => {
    const next = shortDate(m.next_due_on ?? m.schedule.first_on);
    return {
      kind: "topup",
      ic: ruleIcon(m.schedule),
      mandateId: m.mandate_id,
      title: `From ${m.from.display_name}`,
      rule: rowRule(m.schedule),
      when: m.schedule.cadence === "once" ? next : `next ${next}`,
      amount: mandateAmount(m),
    };
  });
  const rows = [...buys, ...topups];
  // "Nothing coming up." is a claim: made only when both reads answered. A partial list is still true, row by row.
  if (rows.length === 0 && (pending === undefined || mandates === undefined)) return undefined;
  return { rows };
}

function orderRow(o: OrderView, nowMs: number): OrderRow {
  return {
    key: `${o.bundle_id}:${o.step_id}`,
    ticker: o.instrument.ticker,
    name: o.instrument.name,
    units: unitsText(o.units_filled),
    price: formatMoney(o.fill_price.value, o.fill_price.currency),
    when: `${o.side === "sell" ? "Sold" : "Bought"} ${whenPast(o.executed_at, nowMs)}`,
    amount: formatMoney(o.filled_amount.value, o.filled_amount.currency),
    // "Settling" needs the bank's settlement date; without it the order is what the ledger knows: filled.
    state: o.settled === true ? "Settled" : o.settles_on !== undefined ? "Settling" : "Filled",
  };
}

/** §4.3 rung 3: a securities line on the trading account, with what its receipt says it bought and at what price. */
function lineRow(f: FilledLine, v: Valuation, nowMs: number): OrderRow {
  const e = f.entry;
  const steps = f.bundle?.view.signing_payload.steps ?? [];
  const results = new Map((f.bundle?.receipt.steps ?? []).map((r) => [r.step_id ?? r.index, r]));
  const resultOf = (s: SecuritiesOrderStep) => results.get(s.step_id) ?? results.get(s.index);
  const orders = steps.filter((s): s is SecuritiesOrderStep => s.type === "securities_order" && resultOf(s)?.state === "executed");
  // The buy this line paid for, by its instrument; none found, the ledger's own words (never another step's figures).
  const step = orders.find((s) => s.instrument.name === e.counterparty_name);
  const r = step === undefined ? undefined : resultOf(step);
  const name = step?.instrument.name ?? e.counterparty_name ?? "";
  const ticker = step?.instrument.ticker ?? v.holdings?.find((h) => h.name === name)?.ticker;
  const units = r?.units_filled;
  const price = step !== undefined && r?.fill_price !== undefined && isMoney(r.fill_price) ? formatMoney(r.fill_price, step.instrument.currency) : undefined;
  return {
    key: step === undefined || f.bundle === undefined ? e.ledger_ref : `${f.bundle.view.bundle_id}:${step.step_id}`,
    ...(ticker === undefined ? {} : { ticker }),
    name,
    ...(units === undefined ? {} : { units: unitsText(units) }),
    ...(price === undefined ? {} : { price }),
    when: `Bought ${whenPast(e.posted_at, nowMs)}`,
    amount: formatMoney(e.amount, e.currency),
    state: "Filled",
  };
}

function ordersOf(read: OrdersRead | undefined, v: Valuation, nowMs: number): OrdersModel | undefined {
  if (read === undefined) return undefined;
  return { rows: read.kind === "route" ? read.orders.map((o) => orderRow(o, nowMs)) : read.lines.map((f) => lineRow(f, v, nowMs)) };
}

/** The tab, from the bank's reads (§5's states). */
export function investModel(r: InvestReads): InvestModel {
  const v = r.valuation;
  const holdings = v.holdings ?? [];
  if (r.settlement === undefined && holdings.length === 0) return { kind: "no-account" };
  const banners: Banners = { locked: r.locked, lapsed: r.lapsed };
  const cash = r.settlement === undefined ? undefined : cashRow(r.settlement, r.pending ?? []);
  const orders = ordersOf(r.orders, v, r.nowMs);
  const coming = comingOf(r.pending, r.mandates, r.nowMs);
  if (holdings.length === 0) {
    return {
      kind: "empty",
      banners,
      ...(cash === undefined ? {} : { cash }),
      ...(orders === undefined || orders.rows.length === 0 ? {} : { orders }),
      ...(coming === undefined || coming.rows.length === 0 ? {} : { coming }),
    };
  }
  const demo = demoOf(v, r.history);
  const base = v.base_currency;
  const chart: ChartSlot =
    r.history.kind === "absent"
      ? { kind: "none" }
      : r.history.kind === "failed"
        ? { kind: "failed" }
        : chartOf(r.history.read, { range: r.range, currency: base, label: "What your shares are worth", column: "Value", demo });
  // The change over the range is the history's own, beside a line drawn from that read; without one (no read, a failed or
  // broken read) the valuation's day change.
  const drawn = r.history.kind === "ok" && (chart.kind === "chart" || chart.kind === "few") ? r.history.read : undefined;
  const change =
    drawn === undefined ? changeOf(v.totals.day_change, v.totals.day_change_pct, base, "today") : changeOf(drawn.change?.value, drawn.change?.pct, base, RANGE_WORDS[r.range].past);
  const note = pricesNote(v);
  return {
    kind: "holdings",
    banners,
    hero: { total: formatMoney(v.totals.market_value, base), demo, ...(change === undefined ? {} : { change }) },
    chart,
    range: r.range,
    ...(note === "" ? {} : { note }),
    holdings: holdings.map((h) => holdingRow(h, base)),
    ...(cash === undefined ? {} : { cash }),
    ...(orders === undefined ? {} : { orders }),
    ...(coming === undefined ? {} : { coming }),
  };
}

export interface HoldingSheetModel {
  readonly id: string;
  readonly ticker: string;
  readonly name: string;
  /** The instrument's own currency: its chart's. */
  readonly currency: string;
  readonly sum: string;
  readonly value: string;
  readonly since?: Pick<Change, "pct" | "tone" | "amount">;
  readonly rows: readonly { readonly k: string; readonly v: string; readonly small?: string }[];
  readonly note?: string;
}

/**
 * The holding sheet's chart (read B): the bank's demo closes in the instrument's own currency — a read that states another
 * currency is not this holding's prices (failed, never printed in the wrong money); none when the bank has no such read.
 */
export function sheetChartOf(state: HistoryState, s: HoldingSheetModel, range: HistoryRange): ChartSlot {
  if (state.kind === "absent") return { kind: "none" };
  if (state.kind === "failed") return { kind: "failed" };
  const currency = state.read.currency ?? state.read.base_currency ?? s.currency;
  if (currency !== s.currency) return { kind: "failed" };
  return chartOf(state.read, { range, currency, label: `${s.name} price`, column: "Price", demo: state.read.price_kind === "demo" });
}

const CURRENCY_WORDS: Readonly<Record<string, { readonly words: string; readonly one: string }>> = { USD: { words: "In US dollars", one: "US$1" } };

/** §6.3: one holding's sheet — the valuation's own figures for it, each in the currency the bank states it in. */
export function holdingSheet(v: Valuation, instrumentId: string): HoldingSheetModel | undefined {
  const h = v.holdings?.find((x) => x.instrument_id === instrumentId);
  if (h === undefined) return undefined;
  const base = v.base_currency;
  const cur = h.currency;
  const units = unitsText(h.units);
  const since = h.unrealised_pl_sgd === undefined ? undefined : changeOf(h.unrealised_pl_sgd, h.unrealised_pl_pct, base, "since you bought");
  const today = h.day_change_sgd === undefined || h.day_change_pct === undefined || !isSignedMoney(h.day_change_sgd) ? undefined : bankPercent(h.day_change_pct, { sign: false });
  const words = CURRENCY_WORDS[cur] ?? { words: `In ${cur}`, one: `${cur} 1` };
  const rows: { readonly k: string; readonly v: string; readonly small?: string }[] = [
    { k: "Price", v: formatMoney(h.price, cur), ...(h.previous_close === undefined ? {} : { small: `Previous close ${formatMoney(h.previous_close, cur)}` }) },
    ...(today === undefined || h.day_change_sgd === undefined ? [] : [{ k: "Today", v: signedAmount(h.day_change_sgd, base), small: `${today} since the previous close` }]),
    ...(h.average_cost === undefined
      ? []
      : [{ k: "Average cost", v: formatMoney(h.average_cost, cur), ...(h.cost === undefined ? {} : { small: `${formatMoney(h.cost, cur)} for ${units}` }) }]),
    ...(h.fx === undefined || h.market_value === undefined || cur === base
      ? []
      : [{ k: words.words, v: formatMoney(h.market_value, cur), small: `Counted at ${words.one} = ${formatRate(h.fx.sgd_per_unit)}` }]),
  ];
  const q = h.quote_as_of;
  return {
    id: h.instrument_id,
    ticker: h.ticker,
    name: h.name,
    currency: cur,
    sum: [h.ticker, h.exchange, units].filter((x) => x !== undefined).join(" · "),
    value: formatMoney(h.market_value_sgd, base),
    ...(since === undefined ? {} : { since: { pct: since.pct, tone: since.tone, amount: since.amount } }),
    rows,
    ...(q === undefined ? {} : { note: `${v.source === "mock" ? "Demo prices" : "Prices"} as of ${asOfWords(q)}` }),
  };
}
